
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Article } from '@/types/models';

type AIFeature = 'elaborate' | 'takeaways' | 'ask' | 'perspective' | 'simplify';

type FeedbackType = 'helpful' | 'not-helpful';

type ConversationMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  relevantSections?: {
    id: number;
    title: string;
  }[];
  timestamp: Date;
};

export type ArticleAIContextType = {
  // Active feature state
  activeFeature: AIFeature | null;
  setActiveFeature: (feature: AIFeature | null) => void;
  
  // Elaborate feature
  elaborateOnParagraph: (paragraphText: string, paragraphIndex: number) => Promise<string | null>;
  elaborations: Record<number, string>;
  elaborationLoadingStates: Record<number, boolean>;
  
  // Key takeaways feature
  generateKeyTakeaways: (articleContent: string, articleTitle: string) => Promise<string[] | null>;
  takeaways: string[] | null;
  isTakeawaysLoading: boolean;
  
  // Ask feature
  askQuestion: (question: string) => Promise<void>;
  conversation: ConversationMessage[];
  isAskLoading: boolean;
  
  // Different perspective feature
  generateAlternativePerspective: () => Promise<string | null>;
  alternativePerspective: string | null;
  isPerspectiveLoading: boolean;
  
  // Simplify feature
  simplifyContent: () => Promise<string | null>;
  simplifiedContent: string | null;
  isSimplifyLoading: boolean;
  
  // Feedback
  provideFeedback: (featureType: AIFeature, contentId: string | number, feedback: FeedbackType) => void;
};

// Create the context
const ArticleAIContext = createContext<ArticleAIContextType | undefined>(undefined);

// Hook to use the context
export const useArticleAI = () => {
  const context = useContext(ArticleAIContext);
  if (!context) {
    throw new Error('useArticleAI must be used within an ArticleAIProvider');
  }
  return context;
};

interface ArticleAIProviderProps {
  children: React.ReactNode;
  article: Article;
}

// Provider component
export const ArticleAIProvider: React.FC<ArticleAIProviderProps> = ({ children, article }) => {
  const { toast } = useToast();
  
  // Active feature
  const [activeFeature, setActiveFeature] = useState<AIFeature | null>(null);
  
  // Elaborate feature state
  const [elaborations, setElaborations] = useState<Record<number, string>>({});
  const [elaborationLoadingStates, setElaborationLoadingStates] = useState<Record<number, boolean>>({});
  
  // Key takeaways feature state
  const [takeaways, setTakeaways] = useState<string[] | null>(null);
  const [isTakeawaysLoading, setIsTakeawaysLoading] = useState(false);
  
  // Ask feature state
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [isAskLoading, setIsAskLoading] = useState(false);
  
  // Different perspective feature state
  const [alternativePerspective, setAlternativePerspective] = useState<string | null>(null);
  const [isPerspectiveLoading, setIsPerspectiveLoading] = useState(false);
  
  // Simplify feature state
  const [simplifiedContent, setSimplifiedContent] = useState<string | null>(null);
  const [isSimplifyLoading, setIsSimplifyLoading] = useState(false);
  
  // Helper function to call OpenAI
  const callOpenAI = async (prompt: string, systemPrompt: string, model = 'gpt-4o-mini', temperature = 0.7, maxTokens = 1000) => {
    try {
      const { data, error } = await supabase.functions.invoke('openai-proxy', {
        body: {
          action: 'chat',
          params: {
            prompt,
            systemPrompt,
            temperature,
            maxTokens,
            model
          }
        }
      });

      if (error) throw new Error(error.message);
      
      return data.response;
    } catch (error: any) {
      console.error('OpenAI API error:', error);
      toast({
        title: "Error",
        description: "Failed to generate AI content. Please try again later.",
        variant: "destructive"
      });
      return null;
    }
  };
  
  // Elaborate on paragraph
  const elaborateOnParagraph = useCallback(async (paragraphText: string, paragraphIndex: number) => {
    setElaborationLoadingStates(prev => ({ ...prev, [paragraphIndex]: true }));
    
    const systemPrompt = "You are an assistant that elaborates on article content with additional context, background information, and explanations. Provide helpful extensions to the given paragraph without changing the original meaning. Keep elaborations concise (2-3 paragraphs maximum) and directly relevant.";
    
    const prompt = `Article title: "${article.title}"\nTags: ${article.tags?.join(', ') || ''}\n\nElaborate on this paragraph with helpful context and background information:\n\n"${paragraphText}"`;
    
    const elaboration = await callOpenAI(prompt, systemPrompt);
    
    if (elaboration) {
      setElaborations(prev => ({ ...prev, [paragraphIndex]: elaboration }));
    }
    
    setElaborationLoadingStates(prev => ({ ...prev, [paragraphIndex]: false }));
    return elaboration;
  }, [article, toast]);
  
  // Extract article content from various possible formats
  const getArticleContent = useCallback(() => {
    let content = '';
    
    // First try to get content from content property
    if (typeof article.content === 'string') {
      content = article.content;
    } 
    // Then try to get content from editor_data property if it exists
    else if (article.content && typeof article.content === 'object') {
      // Handle content as an EditorJS data object
      if ('blocks' in article.content && Array.isArray(article.content.blocks)) {
        content = article.content.blocks
          .map((block: any) => {
            if (block.type === 'paragraph') return block.data?.text || '';
            if (block.type === 'header') return block.data?.text || '';
            return '';
          })
          .join('\n\n');
      }
    }
    // Check for a blocks array directly on the article
    else if ('blocks' in article && Array.isArray((article as any).blocks) && (article as any).blocks.length > 0) {
      content = (article as any).blocks
        .map((block: any) => {
          if (block.type === 'paragraph') return block.content?.text || '';
          if (block.type === 'header') return block.content?.text || '';
          return '';
        })
        .join('\n\n');
    }
    
    return content;
  }, [article]);
  
  // Generate key takeaways
  const generateKeyTakeaways = useCallback(async (articleContent: string, articleTitle: string) => {
    // Check cache first
    const cacheKey = `takeaways-${article.id}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsedCache = JSON.parse(cached);
      setTakeaways(parsedCache);
      return parsedCache;
    }
    
    setIsTakeawaysLoading(true);
    
    const systemPrompt = "You are an assistant that extracts the key points from articles. Create 5 concise bullet points that summarize the main takeaways from the article. Focus on the most important information, insights, and conclusions.";
    
    const prompt = `Article title: "${articleTitle}"\n\nArticle content:\n"${articleContent}"\n\nExtract the 5 most important takeaways from this article.`;
    
    const result = await callOpenAI(prompt, systemPrompt, 'gpt-4o-mini', 0.5, 600);
    
    setIsTakeawaysLoading(false);
    
    if (result) {
      // Process the bullet points
      const bulletPoints = result
        .split(/\n+/)
        .filter(line => line.trim().startsWith('-') || line.trim().startsWith('•'))
        .map(line => line.replace(/^[-•]\s*/, '').trim());
      
      if (bulletPoints.length > 0) {
        // Cache the result
        localStorage.setItem(cacheKey, JSON.stringify(bulletPoints));
        setTakeaways(bulletPoints);
        return bulletPoints;
      }
    }
    
    return null;
  }, [article.id]);
  
  // Ask questions about the article
  const askQuestion = useCallback(async (question: string) => {
    if (!question.trim()) return;
    
    // Add user question to conversation
    const questionId = `q-${Date.now()}`;
    const userMessage: ConversationMessage = {
      id: questionId,
      role: 'user',
      content: question,
      timestamp: new Date()
    };
    
    setConversation(prev => [...prev, userMessage]);
    setIsAskLoading(true);
    
    // Extract article content
    const articleContent = getArticleContent();
    
    const systemPrompt = "You are an assistant that answers questions about an article. Use ONLY information present in the article to answer questions. If the answer cannot be determined from the article, say so clearly. Do not make up information. Include references to relevant sections of the article when possible.";
    
    // Prepare conversation history
    const conversationHistory = conversation.map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`).join('\n\n');
    
    const prompt = `Here is the article content:\n\n${articleContent}\n\nPrevious conversation:\n${conversationHistory}\n\nQuestion: ${question}\n\nPlease answer this question using only information contained within the article.`;
    
    const answer = await callOpenAI(prompt, systemPrompt, 'gpt-4o-mini', 0.7, 800);
    
    setIsAskLoading(false);
    
    if (answer) {
      // Find relevant sections through simple keyword matching
      const relevantSections: { id: number; title: string }[] = [];
      
      // Try to find relevant sections in article content
      const paragraphs = articleContent.split('\n\n');
      paragraphs.forEach((paragraph, index) => {
        if (paragraph.length > 15 && answer.includes(paragraph.substring(0, 15))) {
          relevantSections.push({
            id: index + 1,
            title: paragraph.substring(0, 30) + '...'
          });
        }
      });
      
      const assistantMessage: ConversationMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: answer,
        relevantSections: relevantSections.slice(0, 3), // Limit to top 3 relevant sections
        timestamp: new Date()
      };
      
      setConversation(prev => [...prev, assistantMessage]);
    }
  }, [article, conversation, getArticleContent]);
  
  // Generate alternative perspective
  const generateAlternativePerspective = useCallback(async () => {
    setIsPerspectiveLoading(true);
    
    // Extract article content
    const articleContent = getArticleContent();
    
    const systemPrompt = "You are an assistant that provides alternative viewpoints or interpretations on topics. When given an article, identify the main stance or perspective and offer a balanced, well-reasoned alternative perspective. Be respectful and fair in presenting different viewpoints. Focus on the strongest arguments for the alternative perspective.";
    
    const prompt = `Article title: "${article.title}"\n\nArticle content:\n${articleContent}\n\nIdentify the main perspective in this article and provide a thoughtful, balanced alternative viewpoint. Structure your response as 2-3 paragraphs presenting the strongest arguments for the alternative perspective.`;
    
    const result = await callOpenAI(prompt, systemPrompt, 'gpt-4o-mini', 0.7, 800);
    
    setIsPerspectiveLoading(false);
    
    if (result) {
      setAlternativePerspective(result);
    }
    
    return result;
  }, [article, getArticleContent]);
  
  // Simplify content
  const simplifyContent = useCallback(async () => {
    setIsSimplifyLoading(true);
    
    // Extract article content
    const articleContent = getArticleContent();
    
    const systemPrompt = "You are an assistant that explains complex topics in simple, accessible language. Rewrite the given content at an elementary reading level (ages 5-10). Use short sentences, simple words, and helpful analogies to explain difficult concepts. Avoid technical jargon unless you immediately explain it in simple terms.";
    
    const prompt = `Article title: "${article.title}"\n\nArticle content:\n${articleContent}\n\nPlease rewrite this article in extremely simple language that a 5-year-old would understand. Use analogies for complex concepts and keep sentences short.`;
    
    const result = await callOpenAI(prompt, systemPrompt, 'gpt-4o-mini', 0.7, 1000);
    
    setIsSimplifyLoading(false);
    
    if (result) {
      setSimplifiedContent(result);
    }
    
    return result;
  }, [article, getArticleContent]);
  
  // Provide feedback on generated content
  const provideFeedback = useCallback((featureType: AIFeature, contentId: string | number, feedback: FeedbackType) => {
    // In a real implementation, you would send this feedback to your backend
    console.log(`Feedback for ${featureType}, content ID ${contentId}: ${feedback}`);
    
    toast({
      title: "Thank you for your feedback",
      description: "Your input helps us improve our AI features.",
    });
  }, [toast]);
  
  const value: ArticleAIContextType = {
    activeFeature,
    setActiveFeature,
    elaborateOnParagraph,
    elaborations,
    elaborationLoadingStates,
    generateKeyTakeaways,
    takeaways,
    isTakeawaysLoading,
    askQuestion,
    conversation,
    isAskLoading,
    generateAlternativePerspective,
    alternativePerspective,
    isPerspectiveLoading,
    simplifyContent,
    simplifiedContent,
    isSimplifyLoading,
    provideFeedback
  };
  
  return (
    <ArticleAIContext.Provider value={value}>
      {children}
    </ArticleAIContext.Provider>
  );
};
